/* eslint-disable */
import React, { useState, useEffect } from "react";
import { useParams, useHistory } from "react-router-dom";
import {
  message,
  Card,
  Row,
  Col,
  Tag,
  Button,
  Spin,
  Alert,
  Descriptions,
  Modal,
  Divider,
  Steps,
  Input,
  Space,
} from "antd";
import {
  ArrowLeftOutlined,
  CloseCircleOutlined,
  DollarOutlined,
  CreditCardOutlined,
  CheckCircleOutlined,
  ThunderboltOutlined,
  DeleteOutlined,
  ClockCircleOutlined,
  GiftOutlined,
  PercentageOutlined,
  CalendarOutlined,
  UserOutlined,
  MailOutlined,
  PhoneOutlined,
  IdcardOutlined,
  SafetyCertificateOutlined,
  EnvironmentOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import Header from "../layouts/Header";
import Sidebar from "../layouts/Sidebar";
import { reservationsApi, paymentsApi, promoCodesApi } from "../../apis";
import ResponsiveTable from "../../_components/ResponsiveTable";

const STATUS_COLORS = {
  confirmed: "green",
  cancelled: "red",
  refunded: "orange",
  checked_in: "volcano",
};

const PAYMENT_STATUS_COLORS = {
  success: "green",
  failed: "red",
  pending: "orange",
  refunded: "volcano",
};

// ─────────────────────────────────────────────────────────────────────────────
const ReservationDetail = () => {
  const { id } = useParams();
  const history = useHistory();

  // Detect if current user is admin — determines which payment confirm endpoint to use
  const isAdminUser = (() => {
    try {
      const u = JSON.parse(localStorage.getItem("user") || "null");
      const r = u?.role;
      return (
        (typeof r === "string" ? r : r?.name || "").toLowerCase() === "admin"
      );
    } catch {
      return false;
    }
  })();

  const [menu, setMenu] = useState(false);
  const [loading, setLoading] = useState(true);
  const [reservation, setReservation] = useState(null);
  const [payments, setPayments] = useState([]);
  const [actionLoading, setActionLoading] = useState(false);

  // Modals
  const [cancelModal, setCancelModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);

  // Pay Now flow - with promo code
  const [payModal, setPayModal] = useState(false);
  const [payLoading, setPayLoading] = useState(false);
  const [simLoading, setSimLoading] = useState(null);

  // Confirm payment modal (shows before simulate/confirm to warn about email)
  const [confirmPayModal, setConfirmPayModal] = useState(false);
  const [pendingConfirmPaymentId, setPendingConfirmPaymentId] = useState(null);

  // Promo code state
  const [promoCode, setPromoCode] = useState("");
  const [promoLoading, setPromoLoading] = useState(false);
  const [appliedPromo, setAppliedPromo] = useState(null);
  const [discountedAmount, setDiscountedAmount] = useState(null);
  const [originalAmount, setOriginalAmount] = useState(null);

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [res, pays] = await Promise.all([
        reservationsApi.getById(id),
        paymentsApi.getByReservation(id).catch(() => []),
      ]);
      setReservation(res);
      setPayments(Array.isArray(pays) ? pays : []);

      // Reset promo state when fetching new reservation
      if (res && res.promoCode) {
        setAppliedPromo({
          code: res.promoCode,
          discountPercentage: res.discountPercentage,
          originalTotalPrice: res.originalTotalPrice,
        });
        setDiscountedAmount(res.totalPrice);
        setOriginalAmount(res.originalTotalPrice);
      } else {
        setAppliedPromo(null);
        setDiscountedAmount(null);
        setOriginalAmount(null);
        setPromoCode("");
      }
    } catch {
      message.error("Failed to load reservation");
    } finally {
      setLoading(false);
    }
  };

  // ── Apply Promo Code ──────────────────────────────────────────────────────
  const handleApplyPromoCode = async () => {
    if (!promoCode.trim()) {
      message.warning("Please enter a promo code");
      return;
    }

    try {
      setPromoLoading(true);
      const result = await promoCodesApi.validate(promoCode);

      const originalTotal = Number(reservation.totalPrice);
      const discountAmount = (originalTotal * result.discountPercentage) / 100;
      const newTotal = parseFloat((originalTotal - discountAmount).toFixed(2));

      setAppliedPromo({
        code: promoCode.toUpperCase(),
        discountPercentage: result.discountPercentage,
        promoCodeId: result.promoCodeId,
        originalTotalPrice: originalTotal,
      });
      setDiscountedAmount(newTotal);
      setOriginalAmount(originalTotal);

      message.success(
        `Promo code applied! ${
          result.discountPercentage
        }% discount applied. New total: EGP ${newTotal.toLocaleString()}`,
      );
    } catch (error) {
      const errorMsg = error.response?.data?.message || "Invalid promo code";
      message.error(errorMsg);
      setAppliedPromo(null);
      setDiscountedAmount(null);
      setOriginalAmount(null);
    } finally {
      setPromoLoading(false);
    }
  };

  const handleRemovePromoCode = () => {
    setAppliedPromo(null);
    setDiscountedAmount(null);
    setOriginalAmount(null);
    setPromoCode("");
    message.info("Promo code removed");
  };

  // ── Cancel ────────────────────────────────────────────────────────────────
  const handleCancel = async () => {
    try {
      setActionLoading(true);
      await reservationsApi.cancel(id);
      message.success("Reservation cancelled");
      setCancelModal(false);
      fetchData();
    } catch (e) {
      message.error(e.response?.data?.message || "Failed to cancel");
    } finally {
      setActionLoading(false);
    }
  };

  // ── Delete (hard) ─────────────────────────────────────────────────────────
  const handleDelete = async () => {
    try {
      setActionLoading(true);
      await reservationsApi.remove(id);
      message.success("Reservation permanently deleted");
      history.push("/reservations");
    } catch (e) {
      message.error(e.response?.data?.message || "Failed to delete");
    } finally {
      setActionLoading(false);
    }
  };

  // ── Pay Now: initiate payment with promo code if applied ─────────────────
  const handleInitiatePayment = async () => {
    try {
      setPayLoading(true);

      const paymentData = {
        reservationId: id,
        method: "cash",
      };

      // Include promo code if applied
      if (appliedPromo) {
        paymentData.promoCode = appliedPromo.code;
      }

      const result = await paymentsApi.initiate(paymentData);
      setPayModal(false);

      if (appliedPromo) {
        message.success(
          `Payment confirmed with ${appliedPromo.discountPercentage}% discount! Ticket emailed to ${customerEmail}.`,
        );
      } else {
        message.success(`Cash payment confirmed! Ticket emailed to ${customerEmail}.`);
      }

      // Auto-simulate so the user gets their ticket immediately
      await handleSimulate(result.paymentId);
    } catch (e) {
      message.error(e.response?.data?.message || "Failed to initiate payment");
    } finally {
      setPayLoading(false);
    }
  };

  // ── Confirm payment ──────────────────────────────────────────────────────
  const handleSimulate = async (paymentId) => {
    try {
      setSimLoading(paymentId);
      const r = isAdminUser
        ? await paymentsApi.simulateSuccess(paymentId)
        : await paymentsApi.confirmPayment(paymentId);
      message.success({
        content: (
          <span>
            Payment confirmed! Ticket &amp; confirmation email sent to <strong>{customerEmail}</strong>.
          </span>
        ),
        duration: 5,
      });
      fetchData();
    } catch (e) {
      message.error(e.response?.data?.message || "Payment failed");
    } finally {
      setSimLoading(null);
      setConfirmPayModal(false);
      setPendingConfirmPaymentId(null);
    }
  };

  // ── Derived state ─────────────────────────────────────────────────────────
  const hasPaidPayment = payments.some((p) => p.status === "success");
  const pendingPayments = payments.filter((p) => p.status === "pending");

  const currentTotal =
    discountedAmount !== null ? discountedAmount : reservation?.totalPrice;
  const displayTotal = currentTotal || 0;
  const hasDiscount = appliedPromo !== null && discountedAmount !== null;

  // Payment step for the stepper
  const payStep = (() => {
    if (!reservation) return 0;
    if (reservation.status === "checked_in") return 3;
    if (hasPaidPayment && reservation.status === "confirmed") return 2;
    if (pendingPayments.length) return 1;
    return 0;
  })();

  const customerName = reservation?.user
    ? `${reservation.user.firstName} ${reservation.user.lastName}`
    : reservation?.guestName || "Guest";
  const customerEmail =
    reservation?.user?.email || reservation?.guestEmail || "-";

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className={`main-wrapper ${menu ? "slide-nav" : ""}`}>
      <Header onMenuClick={() => setMenu(!menu)} />
      <Sidebar />
      <div className="page-wrapper">
        <div className="content container-fluid">
          {/* Page header */}
          <div className="page-header">
            <div className="content-page-header">
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <Button
                  icon={<ArrowLeftOutlined />}
                  onClick={() => history.goBack()}
                >
                  Back
                </Button>
                <h5 style={{ margin: 0 }}>Reservation Details</h5>
              </div>

              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {/* Pay Now / Charge — show when no successful payment exists yet */}
                {!hasPaidPayment &&
                  !pendingPayments.length &&
                  reservation?.status !== "cancelled" &&
                  reservation?.status !== "refunded" && (
                    <Button
                      type="primary"
                      style={{
                        backgroundColor: "#7B4D12",
                        borderColor: "#7B4D12",
                      }}
                      icon={<DollarOutlined />}
                      onClick={() => setPayModal(true)}
                    >
                      {reservation?.isAdminReservation
                        ? "Record Cash Payment"
                        : "Pay with Cash"}
                    </Button>
                  )}

                {/* Cancel */}
                {reservation?.status === "confirmed" && (
                  <Button
                    danger
                    icon={<CloseCircleOutlined />}
                    onClick={() => setCancelModal(true)}
                  >
                    Cancel
                  </Button>
                )}

                {/* Delete */}
                <Button
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => setDeleteModal(true)}
                >
                  Delete
                </Button>
              </div>
            </div>
          </div>

          {loading ? (
            <div style={{ textAlign: "center", padding: "80px 0" }}>
              <Spin size="large" />
            </div>
          ) : !reservation ? (
            <Alert message="Reservation not found." type="error" />
          ) : (
            <>
              {/* Payment progress stepper */}
              {reservation.status !== "checked_in" && (
                <Card style={{ borderRadius: 12, marginBottom: 16 }}>
                  <Steps
                    size="small"
                    current={payStep}
                    items={[
                      { title: "Reserved", description: "Booking created" },
                      {
                        title: "Payment",
                        description: pendingPayments.length
                          ? "Awaiting confirmation"
                          : "Pay to confirm",
                      },
                      { title: "Confirmed", description: "Ticket issued" },
                      { title: "Boarded", description: "Checked in" },
                    ]}
                  />
                </Card>
              )}

              {/* Discount Alert if promo applied */}
              {reservation.promoCode && (
                <Alert
                  message="Promo Code Applied"
                  description={
                    <div>
                      Code <strong>{reservation.promoCode}</strong> applied:{" "}
                      {reservation.discountPercentage}% discount saved!
                      Original: EGP{" "}
                      {Number(
                        reservation.originalTotalPrice || 0,
                      ).toLocaleString()}{" "}
                      → Paid: EGP{" "}
                      {Number(reservation.totalPrice).toLocaleString()}
                    </div>
                  }
                  type="success"
                  showIcon
                  icon={<GiftOutlined />}
                  className="mb-3"
                  closable
                />
              )}

              <Row gutter={[16, 16]}>
                {/* ── Left: Reservation info + Ticket + Payments ── */}
                <Col xs={24} lg={16}>
                  {/* Reservation info */}
                  {/* Custom Reservation Info Grid */}
                  <Card
                    title={
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <InfoCircleOutlined style={{ color: "#7B4D12" }} />
                        <span>Core Information</span>
                      </div>
                    }
                    className="premium-card"
                    style={{ marginBottom: 16 }}
                  >
                    <Row gutter={[24, 24]}>
                      <Col xs={12} sm={8} md={6}>
                        <div className="info-label">Reservation ID</div>
                        <div className="info-value">#{reservation.id.slice(0, 8)}</div>
                      </Col>
                      <Col xs={12} sm={8} md={6}>
                        <div className="info-label">Status</div>
                        <Tag
                          color={STATUS_COLORS[reservation.status]}
                          className="status-pulse"
                          style={{ borderRadius: 6, padding: "0 10px" }}
                        >
                          {reservation.status?.replace("_", " ").toUpperCase()}
                        </Tag>
                      </Col>
                      <Col xs={12} sm={8} md={6}>
                        <div className="info-label">Trip Name</div>
                        <div className="info-value">{reservation.tripDate?.trip?.name || "-"}</div>
                      </Col>
                      <Col xs={12} sm={8} md={6}>
                        <div className="info-label">Travel Date</div>
                        <div className="info-value">
                          <CalendarOutlined style={{ marginRight: 6, color: "#7B4D12" }} />
                          {reservation.tripDate?.date || "-"}
                        </div>
                      </Col>
                      <Col xs={12} sm={8} md={6}>
                        <div className="info-label">Boarding Time</div>
                        <div className="info-value">
                          <ClockCircleOutlined style={{ marginRight: 6, color: "#7B4D12" }} />
                          {reservation.tripDate?.trip?.startTime || "-"}
                        </div>
                      </Col>
                      <Col xs={12} sm={8} md={6}>
                        <div className="info-label">Deck Level</div>
                        <Tag color="gold" icon={<EnvironmentOutlined />}>
                          {reservation.deck?.name || "-"}
                        </Tag>
                      </Col>
                      <Col xs={12} sm={8} md={6}>
                        <div className="info-label">Passengers</div>
                        <div className="info-value">
                          <UserOutlined style={{ marginRight: 6, color: "#7B4D12" }} />
                          {reservation.adultSeats} Adult{reservation.adultSeats !== 1 ? "s" : ""}
                          {reservation.childSeats > 0 && (
                            <span style={{ fontSize: 13, color: "#9ca3af", marginLeft: 4 }}>
                              / {reservation.childSeats} Child{reservation.childSeats !== 1 ? "ren" : ""}
                            </span>
                          )}
                        </div>
                      </Col>
                      <Col xs={12} sm={8} md={6}>
                        <div className="info-label">Booking Type</div>
                        <Tag
                          color={reservation.isAdminReservation ? "purple" : "blue"}
                          icon={<SafetyCertificateOutlined />}
                        >
                          {reservation.isAdminReservation ? "Admin" : "Customer"}
                        </Tag>
                      </Col>
                    </Row>
                  </Card>

                   {/* Premium Ticket Display */}
                  {reservation.ticket && hasPaidPayment && (
                    <Card
                      className="premium-card ticket-pass"
                      style={{ marginBottom: 16, padding: "0" }}
                      bodyProps={{ style: { padding: 0 } }}
                    >
                      <Row gutter={0} align="stretch" style={{ minHeight: 180 }}>
                        <Col xs={24} md={16} style={{ padding: 24 }}>
                          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 16 }}>
                            <div>
                              <div className="info-label">Boarding Pass</div>
                              <h3 style={{ margin: 0, fontWeight: 800, color: "#7B4D12" }}>{reservation.tripDate?.trip?.name}</h3>
                            </div>
                            <Tag color="green" icon={<CheckCircleOutlined />}>Verified</Tag>
                          </div>

                          <Row gutter={[16, 16]}>
                            <Col span={12}>
                              <div className="info-label">Passenger</div>
                              <div className="info-value">{customerName}</div>
                            </Col>
                            <Col span={12}>
                              <div className="info-label">Ticket No.</div>
                              <div className="info-value primary">{reservation.ticket.ticketNumber}</div>
                            </Col>
                            <Col span={12}>
                              <div className="info-label">Date & Time</div>
                              <div className="info-value">{reservation.tripDate?.date} @ {reservation.tripDate?.trip?.startTime}</div>
                            </Col>
                            <Col span={12}>
                              <div className="info-label">Deck</div>
                              <div className="info-value">{reservation.deck?.name}</div>
                            </Col>
                          </Row>
                        </Col>

                        <Col xs={24} md={8} style={{
                          padding: 24,
                          background: "#fafafa",
                          borderLeft: "2px dashed #eee",
                          textAlign: "center",
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "center",
                          alignItems: "center"
                        }}>
                          {reservation.ticket.qrCode && (
                            <>
                              <img
                                src={reservation.ticket.qrCode}
                                alt="QR Code"
                                style={{
                                  width: 120,
                                  height: 120,
                                  marginBottom: 8,
                                  padding: 4,
                                  background: "#fff",
                                  border: "1px solid #eee",
                                  borderRadius: 8
                                }}
                              />
                              <div className="info-label" style={{ fontSize: 10 }}>Scan to Board</div>
                            </>
                          )}
                        </Col>
                      </Row>
                    </Card>
                  )}

                  {/* Payment History */}
                  <Card
                    title="Payment History"
                    style={{ borderRadius: 12 }}
                    extra={
                      !hasPaidPayment &&
                      !pendingPayments.length &&
                      reservation?.status !== "cancelled" &&
                      reservation?.status !== "refunded" && (
                        <Button
                          size="small"
                          type="primary"
                          style={{
                            backgroundColor: "#7B4D12",
                            borderColor: "#7B4D12",
                          }}
                          icon={<DollarOutlined />}
                          onClick={() => setPayModal(true)}
                        >
                          {reservation?.isAdminReservation
                            ? "Record Cash Payment"
                            : "Pay with Cash"}
                        </Button>
                      )
                    }
                  >
                    {payments.length === 0 ? (
                      <div
                        style={{
                          textAlign: "center",
                          padding: "24px 0",
                          color: "#9ca3af",
                        }}
                      >
                        {hasPaidPayment
                          ? "Payment complete."
                          : reservation?.isAdminReservation
                          ? "Use 'Record Cash Payment' above to collect payment and issue the ticket."
                          : "No payment recorded yet. Use the button above to pay with cash."}
                      </div>
                    ) : (
                      <ResponsiveTable
                        columns={[
                          {
                            title: "Payment ID",
                            dataIndex: "id",
                            key: "id",
                            render: (id) => (
                              <code style={{ fontSize: 12, color: "#9ca3af" }}>
                                {id?.slice(0, 12)}…
                              </code>
                            ),
                          },
                          {
                            title: "Amount",
                            dataIndex: "amount",
                            key: "amount",
                            render: (amount) => (
                              <div
                                style={{ fontWeight: 700, color: "#7B4D12" }}
                              >
                                EGP {Number(amount).toLocaleString()}
                              </div>
                            ),
                          },
                          {
                            title: "Method",
                            dataIndex: "method",
                            key: "method",
                            render: (method) => (
                              <Tag color="gold">
                                {method?.toUpperCase() || "CASH"}
                              </Tag>
                            ),
                          },
                          {
                            title: "Status",
                            dataIndex: "status",
                            key: "status",
                            render: (status) => (
                              <Tag
                                color={
                                  PAYMENT_STATUS_COLORS[status] || "default"
                                }
                              >
                                {status?.toUpperCase()}
                              </Tag>
                            ),
                          },
                          {
                            title: "Date",
                            dataIndex: "createdAt",
                            key: "createdAt",
                            render: (createdAt) =>
                              new Date(createdAt).toLocaleString(),
                          },
                          ...(isAdminUser || reservation?.isAdminReservation
                            ? [
                                {
                                  title: "Actions",
                                  key: "actions",
                                  render: (_, record) =>
                                    record.status === "pending" && (
                                      <Button
                                        size="small"
                                        type="primary"
                                        style={{
                                          backgroundColor: "#7B4D12",
                                          borderColor: "#7B4D12",
                                        }}
                                        icon={<ThunderboltOutlined />}
                                        loading={simLoading === record.id}
                                        onClick={() => {
                                          setPendingConfirmPaymentId(record.id);
                                          setConfirmPayModal(true);
                                        }}
                                      >
                                        Confirm
                                      </Button>
                                    ),
                                },
                              ]
                            : []),
                        ]}
                        dataSource={payments}
                        rowKey="id"
                        pagination={false}
                        loading={loading}
                      />
                    )}
                  </Card>
                </Col>

                {/* ── Right: Customer info + summary ── */}
                <Col xs={24} lg={8}>
                   <Card
                    title={
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <UserOutlined style={{ color: "#7B4D12" }} />
                        <span>Customer Details</span>
                      </div>
                    }
                    className="premium-card"
                    style={{ marginBottom: 16 }}
                  >
                    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                        <div style={{
                          width: 40,
                          height: 40,
                          borderRadius: 20,
                          background: "#F5ECD7",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "#7B4D12",
                          fontWeight: 700
                        }}>
                          {customerName.charAt(0)}
                        </div>
                        <div>
                          <div className="info-label">Full Name</div>
                          <div className="info-value" style={{ fontSize: 16 }}>{customerName}</div>
                        </div>
                      </div>

                      <Divider style={{ margin: "8px 0" }} />

                      <div>
                        <div className="info-label">Email Address</div>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <MailOutlined style={{ color: "#9ca3af" }} />
                          <a href={`mailto:${customerEmail}`} className="info-value primary">
                            {customerEmail}
                          </a>
                        </div>
                      </div>

                      {(reservation.user?.phone || reservation.guestPhone) && (
                        <div>
                          <div className="info-label">Phone Number</div>
                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <PhoneOutlined style={{ color: "#9ca3af" }} />
                            <a href={`tel:${reservation.user?.phone || reservation.guestPhone}`} className="info-value primary">
                              {reservation.user?.phone || reservation.guestPhone}
                            </a>
                          </div>
                        </div>
                      )}

                      <div>
                        <div className="info-label">Account Type</div>
                        <Tag
                          color={reservation.userId ? "volcano" : "default"}
                          icon={<IdcardOutlined />}
                          style={{ marginTop: 4 }}
                        >
                          {reservation.userId ? "Registered Member" : "Guest Booking"}
                        </Tag>
                      </div>
                    </div>
                  </Card>

                  {/* Amount summary */}
                   <Card
                    className="premium-card"
                    style={{
                      border: "none",
                      background: "linear-gradient(135deg, #7B4D12 0%, #3d2609 100%)",
                      color: "#fff"
                    }}
                  >
                    <div style={{ textAlign: "center", padding: "8px 0" }}>
                      <div
                        style={{
                          fontSize: 12,
                          color: "rgba(255,255,255,0.7)",
                          textTransform: "uppercase",
                          letterSpacing: "0.1em",
                          marginBottom: 4,
                        }}
                      >
                        Final Total
                      </div>
                      <div
                        style={{
                          fontSize: 36,
                          fontWeight: 900,
                          color: "#fff",
                          textShadow: "0 2px 4px rgba(0,0,0,0.2)"
                        }}
                      >
                        EGP {Number(displayTotal).toLocaleString()}
                      </div>

                      {hasDiscount && (
                        <div style={{ marginTop: 8 }}>
                          <Tag color="rgba(255,255,255,0.2)" style={{ color: "#fff", border: "1px solid rgba(255,255,255,0.3)" }}>
                            <del style={{ opacity: 0.7, marginRight: 8 }}>
                              EGP {originalAmount.toLocaleString()}
                            </del>
                            <span style={{ fontWeight: 700 }}>{appliedPromo.discountPercentage}% OFF</span>
                          </Tag>
                        </div>
                      )}

                      <div
                        style={{ fontSize: 13, color: "rgba(255,255,255,0.6)", marginTop: 12 }}
                      >
                        {reservation.adultSeats} Adult{reservation.adultSeats !== 1 ? "s" : ""}
                        {reservation.childSeats > 0 ? ` @ ${reservation.childSeats} Children` : ""}
                      </div>

                      {hasPaidPayment && (
                        <div style={{ marginTop: 16 }}>
                          <Tag
                            color="#fff"
                            style={{ color: "#7B4D12", fontWeight: 700, borderRadius: 20, padding: "2px 16px" }}
                            icon={<CheckCircleOutlined />}
                          >
                            Fully Paid
                          </Tag>
                        </div>
                      )}
                    </div>
                  </Card>
                </Col>
              </Row>
            </>
          )}
        </div>
      </div>

      {/* ── Pay Now Modal with Promo Code ── */}
      <Modal
        title={
          <span>
            <DollarOutlined style={{ marginRight: 8, color: "#f59e0b" }} />
            Cash Payment
          </span>
        }
        open={payModal}
        onCancel={() => {
          setPayModal(false);
          setPromoCode("");
          setAppliedPromo(null);
          setDiscountedAmount(null);
          setOriginalAmount(null);
        }}
        footer={[
          <Button key="cancel" onClick={() => setPayModal(false)}>
            Cancel
          </Button>,
          <Button
            key="submit"
            type="primary"
            style={{
              backgroundColor: "#7B4D12",
              borderColor: "#7B4D12",
            }}
            loading={payLoading}
            onClick={handleInitiatePayment}
          >
            Confirm Payment - EGP {Number(displayTotal).toLocaleString()}
          </Button>,
        ]}
        width={480}
        destroyOnClose
      >
        <Alert
          message="Cash on Boarding"
          description={
            <span>
              Record this cash payment to confirm the reservation. A ticket will be{" "}
              <strong>emailed to {customerEmail}</strong> immediately after confirmation.
            </span>
          }
          type="info"
          showIcon
          className="mb-4"
        />

        {/* Promo Code Section */}
        <Divider orientation="left" style={{ margin: "8px 0 16px" }}>
          <GiftOutlined /> Have a Promo Code?
        </Divider>

        {!appliedPromo ? (
          <Space.Compact style={{ width: "100%", marginBottom: 16 }}>
            <Input
              placeholder="Enter promo code (e.g., SUMMER-AB3F)"
              value={promoCode}
              onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
              onPressEnter={handleApplyPromoCode}
              disabled={promoLoading}
              prefix={<GiftOutlined style={{ color: "#9ca3af" }} />}
            />
            <Button
              type="primary"
              onClick={handleApplyPromoCode}
              loading={promoLoading}
              style={{ backgroundColor: "#7B4D12", borderColor: "#7B4D12" }}
            >
              Apply
            </Button>
          </Space.Compact>
        ) : (
          <div
            style={{
              background: "#f6ffed",
              border: "1px solid #b7eb8f",
              borderRadius: 8,
              padding: "12px",
              marginBottom: 16,
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <div>
                <Tag color="green" icon={<GiftOutlined />}>
                  {appliedPromo.code}
                </Tag>
                <span style={{ marginLeft: 8, fontSize: 13 }}>
                  {appliedPromo.discountPercentage}% discount applied!
                </span>
              </div>
              <Button
                type="link"
                danger
                onClick={handleRemovePromoCode}
                size="small"
              >
                Remove
              </Button>
            </div>
            <div style={{ marginTop: 8, fontSize: 12, color: "#6b7280" }}>
              Original: EGP {originalAmount?.toLocaleString()} → Discounted: EGP{" "}
              {discountedAmount?.toLocaleString()}
            </div>
          </div>
        )}

        <div style={{ padding: "16px 0" }}>
          <div
            style={{
              background: "#fef9e6",
              border: "1px solid #ffe6b3",
              borderRadius: 8,
              padding: "16px",
              textAlign: "center",
            }}
          >
            <div style={{ fontSize: 48, marginBottom: 8 }}>💵</div>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>
              Cash on Boarding
            </div>
            <div style={{ fontSize: 12, color: "#9ca3af" }}>
              Pay when you arrive at the boat
            </div>
          </div>
        </div>

        <Divider style={{ margin: "8px 0 12px" }} />
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            fontWeight: 600,
            marginBottom: 8,
          }}
        >
          <span style={{ color: "#6b7280" }}>Total Amount</span>
          <div style={{ textAlign: "right" }}>
            {hasDiscount && (
              <div style={{ fontSize: 12, color: "#9ca3af" }}>
                <del>EGP {originalAmount?.toLocaleString()}</del>
              </div>
            )}
            <span style={{ color: "#f59e0b", fontSize: 18 }}>
              EGP {Number(displayTotal).toLocaleString()}
            </span>
          </div>
        </div>

        <Alert
          message="Ready to confirm?"
          description={
            <span>
              Clicking confirm will record this payment and instantly email the ticket to{" "}
              <strong style={{ color: "#7B4D12" }}>{customerEmail}</strong>.
            </span>
          }
          type="success"
          showIcon
          icon={<MailOutlined />}
          className="mt-3"
          style={{ background: "#f0f9ff", borderColor: "#bae6fd" }}
        />
      </Modal>

      {/* ── Confirm Payment Modal (warns email will be sent) ── */}
      <Modal
        title={
          <span>
            <CheckCircleOutlined style={{ color: "#7B4D12", marginRight: 8 }} />
            Confirm Cash Payment
          </span>
        }
        open={confirmPayModal}
        onCancel={() => {
          if (!simLoading) {
            setConfirmPayModal(false);
            setPendingConfirmPaymentId(null);
          }
        }}
        footer={[
          <Button
            key="cancel"
            onClick={() => {
              setConfirmPayModal(false);
              setPendingConfirmPaymentId(null);
            }}
            disabled={!!simLoading}
          >
            Cancel
          </Button>,
          <Button
            key="confirm"
            type="primary"
            style={{ backgroundColor: "#7B4D12", borderColor: "#7B4D12" }}
            icon={<MailOutlined />}
            loading={!!simLoading}
            onClick={() => handleSimulate(pendingConfirmPaymentId)}
          >
            Confirm &amp; Send Ticket
          </Button>,
        ]}
        width={460}
        destroyOnClose
      >
        <Alert
          message="Ticket will be emailed automatically"
          description={
            <span>
              Confirming this payment will mark the reservation as{" "}
              <strong>Confirmed</strong> and instantly send the ticket to:{" "}
              <strong style={{ color: "#7B4D12" }}>{customerEmail}</strong>
            </span>
          }
          type="info"
          showIcon
          icon={<MailOutlined />}
          style={{ marginBottom: 16 }}
        />
        <div
          style={{
            background: "#fef9e6",
            border: "1px solid #ffe6b3",
            borderRadius: 8,
            padding: "14px 16px",
            display: "flex",
            flexDirection: "column",
            gap: 6,
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <span style={{ color: "#6b7280" }}>Customer</span>
            <strong>{customerName}</strong>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <span style={{ color: "#6b7280" }}>Email</span>
            <strong style={{ color: "#7B4D12" }}>{customerEmail}</strong>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <span style={{ color: "#6b7280" }}>Amount</span>
            <strong>
              EGP{" "}
              {Number(
                payments.find((p) => p.id === pendingConfirmPaymentId)?.amount || 0
              ).toLocaleString()}
            </strong>
          </div>
        </div>
      </Modal>

      {/* ── Cancel Modal ── */}
      <Modal
        title="Cancel Reservation"
        open={cancelModal}
        onOk={handleCancel}
        onCancel={() => setCancelModal(false)}
        confirmLoading={actionLoading}
        okText="Yes, Cancel It"
        okType="danger"
      >
        <Alert
          message="The reservation will be marked as cancelled. The record is kept."
          type="warning"
          showIcon
          className="mb-3"
        />
        <p>Are you sure you want to cancel this reservation?</p>
      </Modal>

      {/* ── Delete Modal ── */}
      <Modal
        title={
          <span>
            <DeleteOutlined style={{ color: "#ef4444", marginRight: 8 }} />
            Delete Permanently
          </span>
        }
        open={deleteModal}
        onOk={handleDelete}
        onCancel={() => setDeleteModal(false)}
        confirmLoading={actionLoading}
        okText="Delete Forever"
        okType="danger"
      >
        <Alert
          message="This action cannot be undone."
          description="The reservation, its ticket, and all related payment records will be permanently deleted."
          type="error"
          showIcon
          className="mb-3"
        />
        <p>
          Delete reservation <code>{id?.slice(0, 12)}…</code>?
        </p>
      </Modal>
    </div>
  );
};

export default ReservationDetail;